import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useApolloClient, useMutation } from '@apollo/client/react';
import * as R from 'ramda';

import {
  SubItemActiveModal,
  SubItemAttributeUpdate,
  SubItemModalType,
  SubItemPermissions,
  SubItemRootAsset,
  SubItemRowPolicy,
  SubItemsContextValue,
} from '@atom/components/common/subItemTree/SubItemsContext';
import subItemTreeReducer, {
  initTreeState,
  SubItemTreeActionType,
} from '@atom/components/common/subItemTree/subItemTreeReducer';
import textConstants from '@atom/constants/textConstants';
import { WORK_ORDER_UPDATE } from '@atom/graph/work';
import {
  APPROVE_WORK_ORDER_ASSET_ATTRIBUTE_CHANGE,
  APPROVE_WORK_ORDER_ASSET_CHANGES,
  CREATE_WORK_ORDER_ASSET_ELEMENTS,
  DELETE_WORK_ORDER_ASSET,
  GET_WORK_ORDER_ASSET_TREE,
  REJECT_WORK_ORDER_ASSET_ATTRIBUTE_CHANGE,
  REJECT_WORK_ORDER_ASSET_CHANGES,
  UPDATE_WORK_ORDER_ASSET,
} from '@atom/graph/workOrderAsset';
import { useStableCallback } from '@atom/hooks/useStableCallback';
import { useWorkValidations } from '@atom/hooks/useWorkValidations';
import { Snackbar } from '@atom/mui';
import {
  AssetAttributeChangeInput,
  AssetDetail,
  AssetElement,
  AssetElementsCreateInput,
  ChangeType,
} from '@atom/types/asset';
import { SubjectType } from '@atom/types/media';
import { WorkOrderDetailType } from '@atom/types/work';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import {
  isManagerRequiredForWorkMediaEditing,
  isOrgAdminRequiredForAssetAddition,
} from '@atom/utilities/tenantFeatureUtilities';
import { getErrorCode } from '@atom/utilities/validationUtilities';
import { getWorkAssetErrorAttributes } from '@atom/utilities/workValidationUtilities';

import normalizeWorkOrderAssetTree from './normalizeWorkOrderAssetTree';

const EDITABLE_ERROR_CODE = 422;

type UseWorkOrderAssetTreeInput = {
  workOrderDetail: WorkOrderDetailType;
  rootAssetId: string;
  selectedId: string;
  setSelectedId: (id: string) => void;
  refetch: () => void;
  previewView: boolean;
  taskId: string;
  setUnsavedChanges: (hasUnsavedChanges: boolean) => void;
  initialExpanded?: Record<string, boolean>;
};

type UseWorkOrderAssetTreeValue = {
  value: SubItemsContextValue;
  root: AssetElement | undefined;
  loading: boolean;
  saving: boolean;
  approveAll: () => Promise<void>;
};

const useWorkOrderAssetTree = ({
  workOrderDetail,
  rootAssetId,
  selectedId,
  setSelectedId,
  refetch,
  previewView,
  taskId,
  setUnsavedChanges,
  initialExpanded,
}: UseWorkOrderAssetTreeInput): UseWorkOrderAssetTreeValue => {
  const client = useApolloClient();
  const { workValidations, resolveInvalidAssetsError } = useWorkValidations();

  const [tree, dispatch] = useReducer(
    subItemTreeReducer,
    initialExpanded,
    initTreeState,
  );
  const [isNewById, setIsNewById] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState<boolean>(false);

  const [activeModal, setActiveModal] = useState<SubItemActiveModal | null>(
    null,
  );
  const [approvalErrorOpen, setApprovalErrorOpen] = useState<boolean>(false);
  const [scrollToId, setScrollToId] = useState<string | null>(null);

  const openModal = useCallback(
    (type: SubItemModalType, node: AssetElement) =>
      setActiveModal({ type, node }),
    [],
  );
  const onScrolled = useCallback(() => setScrollToId(null), []);
  const onToggleGroup = useCallback(
    (key: string) =>
      dispatch({ type: SubItemTreeActionType.TOGGLE_EXPANDED, data: { key } }),
    [dispatch],
  );

  const [workOrderUpdate] = useMutation(WORK_ORDER_UPDATE);
  const [workOrderAssetUpdate] = useMutation(UPDATE_WORK_ORDER_ASSET);
  const [workOrderAssetDelete] = useMutation(DELETE_WORK_ORDER_ASSET);
  const [workOrderAssetElementsCreate] = useMutation(
    CREATE_WORK_ORDER_ASSET_ELEMENTS,
  );
  const [workOrderAssetChangesApprove] = useMutation(
    APPROVE_WORK_ORDER_ASSET_CHANGES,
  );
  const [workOrderAssetChangesReject] = useMutation(
    REJECT_WORK_ORDER_ASSET_CHANGES,
  );
  const [workOrderAssetAttributeChangeApprove] = useMutation(
    APPROVE_WORK_ORDER_ASSET_ATTRIBUTE_CHANGE,
  );
  const [workOrderAssetAttributeChangeReject] = useMutation(
    REJECT_WORK_ORDER_ASSET_ATTRIBUTE_CHANGE,
  );

  const workOrderId = workOrderDetail.id;
  const root = tree.nodes[rootAssetId];

  const fetchTree = useStableCallback(async () => {
    const { data } = await client.query({
      query: GET_WORK_ORDER_ASSET_TREE,
      variables: { input: { workOrderId, assetId: rootAssetId } },
      fetchPolicy: 'no-cache',
    });

    const normalizedTree = normalizeWorkOrderAssetTree(data.workOrderAssetTree);

    dispatch({ type: SubItemTreeActionType.SET_TREE, data: normalizedTree });
    setIsNewById(normalizedTree.isNewById);

    // A delete, a rejected add, or an approved delete can remove the
    // selected row; fall back to the root.
    if (!normalizedTree.nodes[selectedId]) {
      setSelectedId(rootAssetId);
    }

    return normalizedTree;
  });

  useEffect(() => {
    const load = async () => {
      try {
        const loadedTree = await fetchTree();

        if (selectedId !== rootAssetId && loadedTree.nodes[selectedId]) {
          setScrollToId(selectedId);
        }
      } catch (error) {
        Snackbar.error({
          message: textConstants.GENERIC_APPLICATION_ERROR_TEXT,
        });
      }
    };

    load();
  }, [workOrderId, rootAssetId]);

  // Every write re-reads the whole tree: the server aggregates changeType
  // over each subtree, so one write can change any ancestor's state, and the
  // re-read also refreshes every node's includeChangesBefore cutoff.
  //
  // A 422 only means "attribute is not editable" on approve / reject, so only
  // those writes route it to the approval error modal.
  const mutateAndRefetch = async (
    request: () => Promise<unknown>,
    {
      refetchWorkOrder = false,
      approvalError = false,
    }: { refetchWorkOrder?: boolean; approvalError?: boolean } = {},
  ) => {
    setSaving(true);

    try {
      await request();
      await fetchTree();

      if (refetchWorkOrder) {
        refetch();
      }
    } catch (error) {
      if (approvalError && getErrorCode(error) === EDITABLE_ERROR_CODE) {
        setApprovalErrorOpen(true);
      } else {
        Snackbar.error({
          message: textConstants.GENERIC_APPLICATION_ERROR_TEXT,
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const onRowClick = useStableCallback((node: AssetElement) => {
    setSelectedId(node.id);

    if (node.hasChildren && node.id !== rootAssetId) {
      dispatch({
        type: SubItemTreeActionType.TOGGLE_EXPANDED,
        data: { key: node.id },
      });
    }
  });

  const renameElement = (id: string, name: string) =>
    mutateAndRefetch(() =>
      workOrderAssetUpdate({
        variables: { input: { workOrderId, assetId: id, name } },
      }),
    );

  const deleteElement = (node: AssetElement) =>
    mutateAndRefetch(() =>
      workOrderAssetDelete({
        variables: { input: { workOrderId, assetId: node.id } },
      }),
    );

  const batchCreateElements = (input: AssetElementsCreateInput) =>
    mutateAndRefetch(() =>
      workOrderAssetElementsCreate({
        variables: {
          input: {
            workOrderId,
            assetId: input.assetId,
            elements: input.elements ?? [],
          },
        },
      }),
    );

  const applyChanges = (action: 'approve' | 'reject', node: AssetElement) => {
    const mutation =
      action === 'approve'
        ? workOrderAssetChangesApprove
        : workOrderAssetChangesReject;

    return mutateAndRefetch(
      () =>
        mutation({
          variables: {
            input: {
              workOrderId,
              assetId: node.id,
              includeChangesBefore: node.includeChangesBefore,
              rootAssetId: workOrderDetail.inventoryAssetId,
            },
          },
        }),
      { approvalError: true },
    );
  };

  const approveChanges = useStableCallback((node: AssetElement) =>
    applyChanges('approve', node),
  );

  const rejectChanges = (node: AssetElement) => applyChanges('reject', node);

  const updateElementAttributes = (
    id: string,
    attributes: SubItemAttributeUpdate,
  ) =>
    mutateAndRefetch(
      () =>
        workOrderAssetUpdate({
          variables: {
            input: {
              workOrderId,
              assetId: id,
              attributes: R.map(
                attribute => ({ value: attribute.value }),
                attributes,
              ),
            },
          },
        }),
      { refetchWorkOrder: true },
    );

  const applyAttributeChange = (
    action: 'approve' | 'reject',
    { assetId, attributeId, changeId }: AssetAttributeChangeInput,
  ) => {
    const mutation =
      action === 'approve'
        ? workOrderAssetAttributeChangeApprove
        : workOrderAssetAttributeChangeReject;

    return mutateAndRefetch(
      () =>
        mutation({
          variables: {
            input: {
              workOrderId,
              assetId,
              attributeId,
              changeId,
              rootAssetId: workOrderDetail.inventoryAssetId,
            },
          },
        }),
      { refetchWorkOrder: true },
    );
  };

  const approveAll = async () => {
    setSelectedId(rootAssetId);

    await mutateAndRefetch(
      () =>
        workOrderAssetChangesApprove({
          variables: {
            input: {
              workOrderId,
              assetId: rootAssetId,
              includeChangesBefore: root?.includeChangesBefore,
              approveChangesRecursively: true,
              rootAssetId: workOrderDetail.inventoryAssetId,
            },
          },
        }),
      { approvalError: true },
    );
  };

  const onCoverPhotoToggle = async (mediaId: string) => {
    try {
      await workOrderUpdate({
        variables: {
          workOrder: {
            id: workOrderId,
            mainPhotoId: workOrderDetail.mainPhotoId === mediaId ? '' : mediaId,
          },
        },
      });

      refetch();
    } catch (error) {
      Snackbar.error({ message: textConstants.GENERIC_APPLICATION_ERROR_TEXT });
    }
  };

  const canEdit =
    hasRolePermissions(ROLE_SETS.INSPECTOR) && !workOrderDetail.isClosed;
  // Same rule as the work order Photos / Documents / Videos tabs: some
  // tenants raise media rename and delete to manager.
  const canEditMedia =
    canEdit &&
    hasRolePermissions(
      isManagerRequiredForWorkMediaEditing
        ? ROLE_SETS.MANAGER
        : ROLE_SETS.INSPECTOR,
    );
  const canAddAsset = isOrgAdminRequiredForAssetAddition
    ? hasRolePermissions(ROLE_SETS.ORG_ADMIN)
    : hasRolePermissions(ROLE_SETS.INSPECTOR);
  const canManageChanges =
    hasRolePermissions(ROLE_SETS.MANAGER) && !previewView;

  const permissions = useMemo<SubItemPermissions>(
    () => ({
      canUpdate: canEdit,
      canRename: canEdit,
      canManageChanges,
      moveEnabled: false,
      replaceEnabled: false,
      canUpdateAttributes: canEdit,
      canCreateMedia: canEdit,
      canUpdateMedia: canEditMedia,
      canDeleteMedia: canEditMedia,
    }),
    [canEdit, canEditMedia, previewView],
  );

  // Only elements created in this work order can be deleted, and nothing can
  // be added under an element that is itself pending creation.
  const rowPolicy = useMemo<SubItemRowPolicy>(
    () => ({
      canDelete: node =>
        node.id !== rootAssetId &&
        Boolean(isNewById[node.id]) &&
        node.changeType !== ChangeType.DELETED,
      canAddChildren: node =>
        canAddAsset &&
        (node.id === rootAssetId || node.changeType !== ChangeType.ADDED),
      subtextPrefix: node => node.assetType,
    }),
    [rootAssetId, isNewById, canAddAsset],
  );

  const rootAsset = useMemo<SubItemRootAsset>(
    () => ({
      id: rootAssetId,
      name: root?.name ?? '',
      schemaId: root?.schemaId ?? '',
    }),
    [rootAssetId, root?.name, root?.schemaId],
  );

  const getAttributeDetailsProps = (detail: AssetDetail) => ({
    setUnsavedChanges,
    errorAttributes: getWorkAssetErrorAttributes(
      detail.id,
      taskId,
      workValidations,
    ),
    resolveInvalidAssetsError: (assetId: string, attributeGroupId: string) =>
      resolveInvalidAssetsError(taskId, assetId, attributeGroupId),
    skipManualAttributeValidation: true,
  });

  return {
    value: {
      rootAsset,
      tree,
      permissions,
      rowPolicy,
      rootSelectable: true,
      media: {
        subjectType: SubjectType.WORK_ORDER_ASSET,
        parentSubjectId: workOrderId,
        coverPhotoMediaId: workOrderDetail.mainPhotoId,
        onCoverPhotoToggle: hasRolePermissions(ROLE_SETS.INSPECTOR)
          ? onCoverPhotoToggle
          : undefined,
      },
      getAttributeDetailsProps,
      selectedId,
      setElementId: (id: string | null) => setSelectedId(id ?? rootAssetId),
      onRowClick,
      onToggleGroup,
      scrollToId,
      onScrolled,
      activeModal,
      openModal,
      closeModal: () => setActiveModal(null),
      approvalErrorOpen,
      closeApprovalError: () => setApprovalErrorOpen(false),
      approveChanges,
      rejectChanges,
      renameElement,
      deleteElement,
      batchCreateElements,
      updateElementAttributes,
      applyAttributeChange,
    },
    root,
    loading: !tree.details[rootAssetId],
    saving,
    approveAll,
  };
};

export default useWorkOrderAssetTree;
